import * as mock from './spotsMock';
import { SpotApiError } from './spotsErrors';

const API_URL = process.env.REACT_APP_API_URL;
const API_TOKEN = process.env.REACT_APP_API_TOKEN;
const USE_MOCK = process.env.REACT_APP_SPOTS_MOCK === '1';

export const WEBAPP_URL = process.env.REACT_APP_WEBAPP_URL || 'https://webapp.ibeekeeper.de';

export const SPOTS_ENABLED = process.env.REACT_APP_SPOTS_ENABLED === '1';

export const ASSORTMENT_OPTIONS = [
    { value: 'honey', label: 'Honig' },
    { value: 'wax', label: 'Wachs' },
    { value: 'mead', label: 'Met' },
    { value: 'other', label: 'Sonstiges' }
];

export const SPOT_TYPE_OPTIONS = [
    { value: 4, label: 'Selbstbedienung' },
    { value: 3, label: 'Automat' },
    { value: 2, label: 'Hofladen' }
];

const PHOTO_REJECTION_TEXTS = {
    no_stand: 'Auf dem Bild ist kein Verkaufsstand, Regal, Automat oder Hofladen zu erkennen.',
    person: 'Auf dem Bild ist eine Person zu erkennen.',
    nameplate: 'Auf dem Bild sind Klingel-, Namensschild, Briefkasten oder ein Kennzeichen lesbar.',
    facade: 'Das Bild zeigt überwiegend eine Hausfassade.'
};

export { SpotApiError };

export const describeRejection = (reason) =>
    PHOTO_REJECTION_TEXTS[reason] || 'Das Foto erfüllt die Vorgaben nicht.';

const jsonHeaders = () => ({
    'Content-Type': 'application/json',
    'Token': API_TOKEN
});

const uploadHeaders = () => ({
    'Token': API_TOKEN
});

const transformSpot = (spot) => ({
    id: spot.id,
    lat: parseFloat(spot.lat),
    lng: parseFloat(spot.lon !== undefined ? spot.lon : spot.lng),
    municipality: spot.municipality || '',
    note: spot.note || '',
    assortment: spot.assortment || [],
    photoUrl: spot.photoUrl || spot.photo_url || null,
    spotType: spot.spotType ?? spot.spot_type ?? null,
    confirmations: spot.confirmations ?? 0,
    doubts: spot.doubts ?? 0,
    myVote: spot.myVote ?? spot.my_vote ?? null,
    votingSupported: spot.confirmations !== undefined,
    status: spot.status,
    source: spot.source || 'public',
    createdAt: spot.createdAt || spot.created_at || null,
    claimedLocationId: spot.claimedLocationId || spot.claimed_location_id || null
});

const readError = async (response) => {
    let body = {};
    try {
        body = await response.json();
    } catch {
        body = {};
    }

    const code = body.error || (response.status === 404 ? 'not_found' : 'request_failed');

    if (code === 'duplicate') {
        return new SpotApiError(code, 'Diese Stelle ist bereits erfasst.', {
            spot: body.spot ? transformSpot(body.spot) : null
        });
    }
    if (code === 'photo_rejected') {
        return new SpotApiError(code, 'Das Foto wurde nicht angenommen.', {
            reasons: body.reasons || []
        });
    }
    if (code === 'rate_limited') {
        return new SpotApiError(code, 'Es wurden zu viele Meldungen abgeschickt. Bitte versuche es später noch einmal.', {
            retryAfter: body.retryAfter || null
        });
    }
    if (code === 'not_found') {
        return new SpotApiError(code, 'Dieser Eintrag wurde nicht gefunden.');
    }
    if (code === 'already_voted') {
        return new SpotApiError(code, 'Du hast für diesen Eintrag schon abgestimmt.', {
            spot: body.spot ? transformSpot(body.spot) : null
        });
    }
    if (code === 'not_votable') {
        return new SpotApiError(code, 'Für diesen Eintrag lässt sich nicht abstimmen.');
    }

    return new SpotApiError(code, body.message || 'Die Anfrage ist fehlgeschlagen.');
};

export const fetchReportedSpots = async (filters = {}) => {
    if (USE_MOCK) return mock.fetchReportedSpots(filters);

    const response = await fetch(`${API_URL}/spots`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
            filters: {
                limit: 200,
                offset: 0,
                ...filters
            }
        })
    });

    if (!response.ok) throw await readError(response);

    const data = await response.json();
    return {
        meta: data.meta || { total: 0, limit: 200, offset: 0 },
        spots: (data.spots || []).map(transformSpot)
    };
};

export const fetchReportedSpot = async (id, editToken) => {
    if (USE_MOCK) return mock.fetchReportedSpot(id, editToken);

    const query = editToken ? `?editToken=${encodeURIComponent(editToken)}` : '';
    const response = await fetch(`${API_URL}/spots/${encodeURIComponent(id)}${query}`, {
        method: 'GET',
        headers: jsonHeaders()
    });

    if (!response.ok) throw await readError(response);

    const data = await response.json();
    if (!data.spot) throw new SpotApiError('not_found', 'Dieser Eintrag wurde nicht gefunden.');

    return {
        spot: transformSpot(data.spot),
        canEdit: Boolean(data.canEdit)
    };
};

export const reportSpot = async ({ photo, lat, lng, note, assortment, email, spotType, language }) => {
    if (USE_MOCK) return mock.reportSpot({ photo, lat, lng, note, assortment, email, spotType });

    const form = new FormData();
    form.append('photo', photo);
    form.append('lat', String(lat));
    form.append('lon', String(lng));
    if (note) form.append('note', note);
    if (email) form.append('email', email);
    if (spotType) form.append('spotType', String(spotType));
    if (language) form.append('language', language);
    (assortment || []).forEach(value => form.append('assortment[]', value));

    const response = await fetch(`${API_URL}/spots/report`, {
        method: 'POST',
        headers: uploadHeaders(),
        body: form
    });

    if (!response.ok) throw await readError(response);

    const data = await response.json();
    return {
        spot: transformSpot(data.spot),
        editToken: data.editToken
    };
};

export const withdrawSpot = async (id, editToken) => {
    if (USE_MOCK) return mock.withdrawSpot(id, editToken);

    const response = await fetch(`${API_URL}/spots/${encodeURIComponent(id)}/withdraw`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ editToken })
    });

    if (!response.ok) throw await readError(response);
    return true;
};

export const voteSpot = async (id, vote) => {
    if (USE_MOCK) return mock.voteSpot(id, vote);

    const response = await fetch(`${API_URL}/spots/${encodeURIComponent(id)}/vote`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ vote })
    });

    if (!response.ok) throw await readError(response);

    const data = await response.json();
    return { spot: transformSpot(data.spot), voted: data.voted || vote };
};

export const submitObjection = async (id, { contact, message }) => {
    if (USE_MOCK) return mock.submitObjection(id, { contact, message });

    const response = await fetch(`${API_URL}/spots/${encodeURIComponent(id)}/objection`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ contact, message })
    });

    if (!response.ok) throw await readError(response);
    return true;
};

export const buildSpotPath = (id, editToken) =>
    editToken ? `/gemeldet/${id}?t=${encodeURIComponent(editToken)}` : `/gemeldet/${id}`;

export const buildClaimUrl = (id) =>
    `${WEBAPP_URL}/verkaufsstelle-uebernehmen/${encodeURIComponent(id)}`;
